import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { Invoice, InvoiceStatus } from '../types';

interface InvoicesContextType {
  invoices: Invoice[];
  loading: boolean;
  addInvoice: (invoice: Omit<Invoice, 'id'>) => Promise<Invoice>;
  updateInvoice: (invoice: Invoice) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  getInvoiceById: (id: string) => Invoice | undefined;
  getInvoicesByProject: (projectId: string) => Invoice[];
  generateInvoiceNumber: () => Promise<string>;
  markInvoiceAsPaid: (id: string) => Promise<void>;
  markInvoiceAsSent: (id: string) => Promise<void>;
  sendInvoiceEmail: (invoiceId: string, recipientEmail: string, message?: string, pdfBase64?: string, pdfFilename?: string) => Promise<any>;
  fetchInvoices: () => Promise<void>;
}

const InvoicesContext = createContext<InvoicesContextType | undefined>(undefined);

export const useInvoices = () => {
  const context = useContext(InvoicesContext);
  if (!context) {
    throw new Error('useInvoices must be used within an InvoicesProvider');
  }
  return context;
};

export const InvoicesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch invoices from Supabase when authenticated and org is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchInvoices();
    }
  }, [isAuthenticated, currentOrganization]);

  const fetchInvoices = async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          project:projects(title),
          client:clients(name, company, address, phone, email)
        `)
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false })
        .limit(100); // OPTIMIZED: Prevent loading huge datasets

      if (error) {
        console.error('Error fetching invoices:', error);
        return;
      }

      if (data) {
        // Transform Supabase data to Invoice format
        const transformedInvoices: Invoice[] = data.map(invoice => ({
          id: invoice.id,
          invoiceNumber: invoice.invoice_number,
          projectId: invoice.project_id,
          client: {
            name: invoice.client?.name || 'Unknown Client',
            address: invoice.client?.address || '',
            phone: invoice.client?.phone,
            email: invoice.client?.email,
            company: invoice.client?.company
          },
          status: invoice.status,
          createdAt: invoice.created_at,
          dueDate: invoice.due_date,
          sentDate: invoice.sent_date,
          paidDate: invoice.paid_date,
          subtotal: parseFloat(invoice.subtotal),
          taxRate: parseFloat(invoice.tax_rate),
          taxAmount: parseFloat(invoice.tax_amount),
          total: parseFloat(invoice.total),
          paymentTerms: invoice.payment_terms,
          notes: invoice.notes,
          companyInfo: invoice.company_info,
          laborItems: invoice.labor_items || [],
          materialItems: invoice.material_items || [],
          aadeData: invoice.aade_data
        }));

        setInvoices(transformedInvoices);
      }
    } catch (error) {
      console.error('Error in fetchInvoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceNumber = async (): Promise<string> => {
    if (!currentOrganization) {
      // Fallback to timestamp-based number if no org
      const year = new Date().getFullYear();
      const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
      return `INV-${year}${month}-${Date.now().toString().slice(-4)}`;
    }

    try {
      const year = new Date().getFullYear();
      const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
      
      // Get the count of invoices for this month FOR THIS ORGANIZATION
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number')
        .eq('organization_id', currentOrganization.id)
        .like('invoice_number', `INV-${year}${month}-%`)
        .order('invoice_number', { ascending: false });

      if (error) {
        console.error('Error generating invoice number:', error);
        // Fallback to timestamp-based number with more digits
        return `INV-${year}${month}-${Date.now().toString().slice(-4)}`;
      }

      // Find the highest sequence number and increment
      let nextSequence = 1;
      if (data && data.length > 0) {
        // Extract sequence number from the most recent invoice
        const lastInvoice = data[0].invoice_number;
        const match = lastInvoice.match(/INV-\d{6}-(\d+)/);
        if (match && match[1]) {
          nextSequence = parseInt(match[1]) + 1;
        } else {
          nextSequence = data.length + 1;
        }
      }

      const sequence = nextSequence.toString().padStart(3, '0');
      const invoiceNumber = `INV-${year}${month}-${sequence}`;
      
      console.log('🔢 Generated invoice number:', invoiceNumber);
      return invoiceNumber;
    } catch (error) {
      console.error('Error in generateInvoiceNumber:', error);
      // Fallback to timestamp-based number with more digits for uniqueness
      const year = new Date().getFullYear();
      const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
      const timestamp = Date.now().toString().slice(-4);
      return `INV-${year}${month}-T${timestamp}`;
    }
  };

  const sendInvoiceEmail = async (invoiceId: string, recipientEmail: string, message?: string, pdfBase64?: string, pdfFilename?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-invoice-email', {
        body: {
          invoiceId,
          recipientEmail,
          message,
          pdfBase64,
          pdfFilename
        }
      });

      if (error) {
        console.error('Error sending invoice email:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in sendInvoiceEmail:', error);
      throw error;
    }
  };

  const addInvoice = async (invoice: Omit<Invoice, 'id'>) => {
    if (!currentOrganization) {
      console.error('No organization selected');
      throw new Error('No organization selected');
    }

    try {
      console.log('📝 Creating invoice:', invoice.invoiceNumber);

      // Find or create client
      let clientId: string;
      let existingClient: any = null;

      console.log('🔍 Looking for existing client...');
      console.log('  Email:', invoice.client.email);
      console.log('  Name:', invoice.client.name);
      console.log('  Organization:', currentOrganization.id);

      if (invoice.client.email) {
        const { data, error: lookupError } = await supabase
          .from('clients')
          .select('id')
          .eq('email', invoice.client.email)
          .eq('organization_id', currentOrganization.id)
          .maybeSingle();
        
        if (lookupError) {
          console.warn('⚠️ Error looking up client by email:', lookupError);
        } else if (data) {
          existingClient = data;
          console.log('✅ Found existing client by email:', data.id);
        }
      }
      
      // If no client found by email, try by name and address
      if (!existingClient) {
        const { data, error: lookupError } = await supabase
          .from('clients')
          .select('id')
          .eq('name', invoice.client.name)
          .eq('address', invoice.client.address)
          .eq('organization_id', currentOrganization.id)
          .maybeSingle();
        
        if (lookupError) {
          console.warn('⚠️ Error looking up client by name/address:', lookupError);
        } else if (data) {
          existingClient = data;
          console.log('✅ Found existing client by name/address:', data.id);
        } else {
          console.log('ℹ️ No existing client found');
        }
      }

      if (existingClient) {
        clientId = existingClient.id;
        console.log('✅ Using existing client:', clientId);
      } else {
        console.log('➕ Creating new client');

        // Create new client
        const { data: newClient, error: clientError } = await supabase
          .from('clients')
          .insert({
            name: invoice.client.name,
            address: invoice.client.address,
            phone: invoice.client.phone,
            email: invoice.client.email || null, // Use null if no email
            company: invoice.client.company,
            organization_id: currentOrganization.id
          })
          .select()
          .single();

        if (clientError) {
          // If duplicate (organization_id, email) error, try to find and use that client
          if (clientError.code === '23505' && (
            clientError.message.includes('clients_organization_email_key') ||
            clientError.message.includes('clients_email_key')
          )) {
            console.warn('⚠️ Client with this email already exists in your organization, attempting to find and use it...');
            
            // Try to find the client with this email in our organization
            const { data: existingByEmail } = await supabase
              .from('clients')
              .select('id')
              .eq('email', invoice.client.email)
              .eq('organization_id', currentOrganization.id)
              .maybeSingle();
            
            if (existingByEmail) {
              clientId = existingByEmail.id;
              console.log('✅ Using existing client with same email:', clientId);
            } else {
              // This shouldn't happen, but handle it gracefully
              console.error('❌ Duplicate email error but client not found - possible race condition');
              throw new Error(`A client with email "${invoice.client.email}" already exists but could not be retrieved. Please try again.`);
            }
          } else {
            console.error('❌ Error creating client:', clientError);
            throw clientError;
          }
        } else if (newClient) {
          clientId = newClient.id;
          console.log('✅ Created new client:', clientId);
        } else {
          throw new Error('Failed to create client - no data returned');
        }
      }

      console.log('💾 Inserting invoice into database...');

      const { data, error } = await supabase
        .from('invoices')
        .insert({
          invoice_number: invoice.invoiceNumber,
          project_id: invoice.projectId,
          client_id: clientId,
          organization_id: currentOrganization.id,
          status: invoice.status,
          due_date: invoice.dueDate,
          sent_date: invoice.sentDate || null,
          paid_date: invoice.paidDate || null,
          subtotal: invoice.subtotal,
          tax_rate: invoice.taxRate,
          tax_amount: invoice.taxAmount,
          total: invoice.total,
          payment_terms: invoice.paymentTerms,
          notes: invoice.notes || null,
          company_info: invoice.companyInfo,
          labor_items: invoice.laborItems,
          material_items: invoice.materialItems,
          aade_data: invoice.aadeData || null
        })
        .select(`
          *,
          project:projects(title),
          client:clients(name, company, address, phone, email)
        `)
        .single();

      if (error) {
        console.error('❌ Error adding invoice:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      if (!data) {
        throw new Error('Failed to create invoice - no data returned');
      }

      if (data) {
        const newInvoice: Invoice = {
          id: data.id,
          invoiceNumber: data.invoice_number,
          projectId: data.project_id,
          client: {
            name: data.client?.name || 'Unknown Client',
            address: data.client?.address || '',
            phone: data.client?.phone,
            email: data.client?.email,
            company: data.client?.company
          },
          status: data.status,
          createdAt: data.created_at,
          dueDate: data.due_date,
          sentDate: data.sent_date,
          paidDate: data.paid_date,
          subtotal: parseFloat(data.subtotal),
          taxRate: parseFloat(data.tax_rate),
          taxAmount: parseFloat(data.tax_amount),
          total: parseFloat(data.total),
          paymentTerms: data.payment_terms,
          notes: data.notes,
          companyInfo: data.company_info,
          laborItems: data.labor_items || [],
          materialItems: data.material_items || [],
          aadeData: data.aade_data
        };

        setInvoices(prev => [...prev, newInvoice]);
        console.log('✅ Invoice created successfully:', newInvoice.id);
        return newInvoice;
      }

      throw new Error('Failed to create invoice - no data returned after insert');
    } catch (error) {
      console.error('❌ Error in addInvoice:', error);
      // Re-throw with user-friendly message
      if (error instanceof Error) {
        throw new Error(`Failed to create invoice: ${error.message}`);
      }
      throw error;
    }
  };

  const updateInvoice = async (updatedInvoice: Invoice) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update({
          invoice_number: updatedInvoice.invoiceNumber,
          status: updatedInvoice.status,
          due_date: updatedInvoice.dueDate,
          sent_date: updatedInvoice.sentDate,
          paid_date: updatedInvoice.paidDate,
          subtotal: updatedInvoice.subtotal,
          tax_rate: updatedInvoice.taxRate,
          tax_amount: updatedInvoice.taxAmount,
          total: updatedInvoice.total,
          payment_terms: updatedInvoice.paymentTerms,
          notes: updatedInvoice.notes,
          company_info: updatedInvoice.companyInfo,
          labor_items: updatedInvoice.laborItems,
          material_items: updatedInvoice.materialItems,
          aade_data: updatedInvoice.aadeData
        })
        .eq('id', updatedInvoice.id);

      if (error) {
        console.error('Error updating invoice:', error);
        throw error;
      }

      setInvoices(prev => 
        prev.map(invoice => 
          invoice.id === updatedInvoice.id ? updatedInvoice : invoice
        )
      );
    } catch (error) {
      console.error('Error in updateInvoice:', error);
      throw error;
    }
  };

  const deleteInvoice = async (id: string) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting invoice:', error);
        throw error;
      }

      setInvoices(prev => prev.filter(invoice => invoice.id !== id));
    } catch (error) {
      console.error('Error in deleteInvoice:', error);
      throw error;
    }
  };

  const getInvoiceById = (id: string) => {
    return invoices.find(invoice => invoice.id === id);
  };

  const getInvoicesByProject = (projectId: string) => {
    return invoices.filter(invoice => invoice.projectId === projectId);
  };

  const markInvoiceAsPaid = async (id: string) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'paid',
          paid_date: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        console.error('Error marking invoice as paid:', error);
        throw error;
      }

      setInvoices(prev => 
        prev.map(invoice => 
          invoice.id === id 
            ? { ...invoice, status: 'paid' as InvoiceStatus, paidDate: new Date().toISOString() }
            : invoice
        )
      );
    } catch (error) {
      console.error('Error in markInvoiceAsPaid:', error);
      throw error;
    }
  };

  const markInvoiceAsSent = async (id: string) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'sent',
          sent_date: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        console.error('Error marking invoice as sent:', error);
        throw error;
      }

      setInvoices(prev => 
        prev.map(invoice => 
          invoice.id === id 
            ? { ...invoice, status: 'sent' as InvoiceStatus, sentDate: new Date().toISOString() }
            : invoice
        )
      );
    } catch (error) {
      console.error('Error in markInvoiceAsSent:', error);
      throw error;
    }
  };

  return (
    <InvoicesContext.Provider value={{
      invoices,
      loading,
      addInvoice,
      updateInvoice,
      deleteInvoice,
      getInvoiceById,
      getInvoicesByProject,
      generateInvoiceNumber,
      markInvoiceAsPaid,
      markInvoiceAsSent,
      sendInvoiceEmail,
      fetchInvoices
    }}>
      {children}
    </InvoicesContext.Provider>
  );
};